import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { isValidPhoneNumber } from "react-phone-number-input";
import { Button } from "@/components/ui/button";
import type { ShippingAddress } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CheckoutEmailStep } from "./checkout/CheckoutEmailStep";
import { CheckoutShippingStep } from "./checkout/CheckoutShippingStep";
import { HostedCheckoutPayment } from "./checkout/CheckoutPaymentStep";
import { DeliveryTypeEnum } from "@/types/DeliveryType";
import { getContrastTextColor, checkoutStripeElementsAppearance } from "@/utils/colorUtils";
import { shippingAddressWithoutPhone } from "@/lib/shippingAddressWithoutPhone";
import { getShippingForPhysicalCart } from "@/lib/checkoutShipping";
import { normalizeCheckoutPhone } from "@/lib/normalizeCheckoutPhone";
import {
  initMetaPixelCheckout,
  metaPixelCheckoutPayload,
  metaPixelNoScriptPageViewSrc,
  trackMetaPixel,
} from "@/lib/metaPixel";
import {
  variantIsPurchasable as isVariantInStock,
} from "@/lib/validateCheckoutInventory";

interface ProductVariant {
  _id: string;
  name: string;
  quantity?: number;
  price?: number;
}

function variantIsPurchasableForProduct(
  product: Product,
  variant: ProductVariant,
): boolean {
  return isVariantInStock({
    variant,
    inventoryQuantity: product.inventoryQuantity,
  });
}

interface Product {
  _id: string;
  name?: string;
  description?: string;
  price: number;
  payWhatYouCan?: boolean;
  deliveryType?: string;
  inventoryQuantity?: number;
  quantityRules?: { minimum: number; maximum: number };
  variants?: ProductVariant[];
  disableRemove?: boolean;
}

function unitPriceCents(p: Product, variantId?: string): number {
  const vid = variantId?.trim();
  const v = vid && p.variants?.find((x) => String(x._id) === vid);
  if (v) {
    if (v.price != null) return Math.round(v.price);
    return Math.round(p.price ?? 0);
  }
  return Math.round(p.price ?? 0);
}

function resolveCartLineVariantId({
  product,
  checkoutLineItem,
  lineVariantIds,
  displayIndex,
}: {
  product: Product;
  checkoutLineItem: CheckoutClientLineItem | undefined;
  lineVariantIds: Record<number, string>;
  displayIndex: number;
}): string | undefined {
  const pidOk =
    checkoutLineItem && String(checkoutLineItem.productId) === String(product._id);
  const variants = product.variants;
  let vid: string | undefined;
  if (variants?.length) {
    const sel = lineVariantIds[displayIndex]?.trim();
    const selVariant = sel ? variants.find((x) => String(x._id) === sel) : undefined;
    if (selVariant && variantIsPurchasableForProduct(product, selVariant)) vid = sel;
    else {
      const fromLine = pidOk ? checkoutLineItem.variantId?.trim() : undefined;
      const vMatch =
        fromLine != null ? variants.find((x) => String(x._id) === fromLine) : undefined;
      vid = vMatch && variantIsPurchasableForProduct(product, vMatch) ? fromLine : undefined;
    }
    if (!vid && variants.length === 1) {
      const only = variants[0];
      if (variantIsPurchasableForProduct(product, only)) vid = String(only._id);
    }
  } else {
    vid = pidOk ? checkoutLineItem.variantId?.trim() : undefined;
  }
  const v = vid ? product.variants?.find((x) => String(x._id) === vid) : undefined;
  return v && vid ? vid : undefined;
}

function buildLineItemsPayload({
  products,
  visibleProductIndices,
  lineVariantIds,
  checkoutLineItems,
  quantities,
}: {
  products: Product[];
  visibleProductIndices: number[];
  lineVariantIds: Record<number, string>;
  checkoutLineItems: CheckoutClientLineItem[];
  quantities: Record<string, number>;
}): CheckoutClientLineItem[] {
  const retainedSorted = [...visibleProductIndices].sort((a, b) => a - b);
  return retainedSorted.map((prodIdx) => {
    const dispIdx = visibleProductIndices.indexOf(prodIdx);
    const p = products[prodIdx];
    const line = checkoutLineItems[prodIdx];
    const variantId = resolveCartLineVariantId({
      product: p,
      checkoutLineItem: line,
      lineVariantIds,
      displayIndex: dispIdx,
    });
    const min = p.quantityRules?.minimum ?? 1;
    const max = p.quantityRules?.maximum ?? 10;
    const rawQty = quantities[p._id];
    const fromQuantities =
      typeof rawQty === "number" && Number.isFinite(rawQty) && rawQty > 0
        ? Math.round(rawQty)
        : undefined;
    const pidOk = line && String(line.productId) === String(p._id);
    const fromLine =
      pidOk &&
      typeof line.quantity === "number" &&
      Number.isFinite(line.quantity) &&
      line.quantity > 0
        ? Math.round(line.quantity)
        : min;
    const quantity = Math.max(min, Math.min(max, fromQuantities ?? fromLine));
    return {
      productId: p._id,
      quantity,
      ...(variantId ? { variantId } : {}),
    };
  });
}

interface CheckoutClientLineItem {
  productId: string;
  quantity?: number;
  variantId?: string;
}

function buildInitialQuantities({
  products,
  checkoutLineItems,
}: {
  products: Product[];
  checkoutLineItems: CheckoutClientLineItem[];
}): Record<string, number> {
  const initialQuantities: Record<string, number> = {};
  products.forEach((product, index) => {
    const line = checkoutLineItems[index];
    const min = product.quantityRules?.minimum ?? 1;
    const max = product.quantityRules?.maximum ?? 10;
    const pidOk = line && String(line.productId) === String(product._id);
    const fromDoc =
      pidOk &&
      typeof line.quantity === "number" &&
      Number.isFinite(line.quantity) &&
      line.quantity > 0
        ? Math.round(line.quantity)
        : min;
    initialQuantities[product._id] = Math.max(min, Math.min(max, fromDoc));
  });
  return initialQuantities;
}

interface ContactCustomFieldDef {
  key: string;
  label: string;
  required?: boolean;
  options: string[];
}

interface CheckoutSessionSnapshot {
  _id?: string;
  savePaymentMethod?: boolean;
  submitBtnText?: string;
  customerEmail?: string;
  customerPhone?: string;
  orderBumpOptIn?: boolean;
  orderBumpsOptIn?: boolean[];
  orderBumpOffer?: {
    title: string;
    description?: string;
    unitPriceCents: number;
    quantity: number;
    maxQuantity?: number;
    lineTotalCents: number;
    imageUrl?: string;
    preSelected?: boolean;
  };
  orderBumpOffers?: Array<{
    title: string;
    description?: string;
    unitPriceCents: number;
    quantity: number;
    maxQuantity?: number;
    lineTotalCents: number;
    imageUrl?: string;
    preSelected?: boolean;
  }>;
  tippingOffer?: {
    title: string;
    description?: string;
    defaultSelected?: boolean;
  };
  shipping?: {
    name: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      country: string;
      postal_code: string;
    };
  };
  contactCustomFields?: ContactCustomFieldDef[];
  contactCustomFieldValues?: Record<string, string>;
}

function buildInitialCustomFieldValues(session: CheckoutSessionSnapshot | undefined) {
  const out: Record<string, string> = {};
  const defs = session?.contactCustomFields;
  if (!defs?.length) return out;
  const saved = session?.contactCustomFieldValues ?? {};
  for (const d of defs) {
    out[d.key] = saved[d.key] ?? "";
  }
  return out;
}

function shippingSnapshotToStripe(
  checkoutSession: CheckoutSessionSnapshot | undefined
): { address: ShippingAddress | null; addressComplete: boolean } {
  const s = checkoutSession?.shipping;
  if (!s?.address) return { address: null, addressComplete: false };
  const a = s.address;
  const address: ShippingAddress = {
    name: s.name,
    address: {
      line1: a.line1 ?? "",
      line2: a.line2,
      city: a.city ?? "",
      state: a.state ?? "",
      country: a.country ?? "",
      postal_code: a.postal_code ?? "",
    },
  };
  const addressComplete = Boolean(
    a.line1?.trim() &&
      a.city?.trim() &&
      a.postal_code?.trim() &&
      a.country?.trim()
  );
  return { address, addressComplete };
}

interface CheckoutFlowProps {
  checkoutId: string;
  products: Product[];
  /** Same order as products from checkout API */
  lineItems?: CheckoutClientLineItem[];
  checkoutSession?: CheckoutSessionSnapshot;
  stripe: {
    publishableKey: string;
    accountId?: string;
  } | null;
  /** From GET checkout when the stored PaymentIntent has last_payment_error */
  paymentIntentLastErrorMessage?: string;
  /** From GET checkout when the stored SetupIntent has last_setup_error */
  setupIntentLastErrorMessage?: string;
  checkoutSettings: {
    tipping?: {
      enabled: boolean;
      defaultSelected?: boolean;
      amount1?: number;
      amount2?: number;
      amount3?: number;
      title?: string;
      description?: string;
    };
    colors?: {
      backgroundColor?: string;
      buttonColor?: string;
      accentColor?: string;
    };
    shippingProfiles?: Array<{
      name: string;
      productIds: string[];
      shippingZones: Array<{
        name: string;
        countries: string[];
        prices: Array<{ name: string; price: number }>;
      }>;
    }>;
    shippingProtection?: {
      enabled: boolean;
      price: number;
    };
    metaPixelId?: string;
  } | null;
}

const STEP_MAP = { contact: 1, shipping: 2, payment: 3 } as const;

function contactPrefillFromSearchParams(searchParams: URLSearchParams) {
  const email = searchParams.get("email")?.trim() ?? "";
  const rawPhone = searchParams.get("phone")?.trim() ?? "";
  const parsed = normalizeCheckoutPhone(rawPhone);
  const phone = parsed.ok ? (parsed.e164 ?? "") : rawPhone;

  return { email, phone };
}

function contactPrefillFromSessionStorage() {
  if (typeof sessionStorage === "undefined") {
    return { name: "", email: "", phone: "" };
  }
  const name = sessionStorage.getItem("name")?.trim() ?? "";
  const email = sessionStorage.getItem("email")?.trim() ?? "";
  const rawPhone = sessionStorage.getItem("phone")?.trim() ?? "";
  const parsed = normalizeCheckoutPhone(rawPhone);
  const phone = parsed.ok ? (parsed.e164 ?? "") : rawPhone;
  return { name, email, phone };
}

interface UIBumpGroup {
  id: string;
  productId?: string;
  title: string;
  description?: string;
  imageUrl?: string;
  variants?: Array<{ _id: string; name: string; price?: number }>;
  unitPriceCents: number;
  preSelected?: boolean;
  checked: boolean;
  quantity: number;
  selectedVariantIds: string[];
  maxQuantity?: number;
}

function serializeBumpGroups(groups: UIBumpGroup[]) {
  const orderBumpsPayload: any[] = [];
  const optInsPayload: boolean[] = [];

  groups.forEach((group) => {
    if (group.productId) {
      const counts: Record<string, number> = {};
      group.selectedVariantIds.forEach((vid) => {
        const key = vid || "";
        counts[key] = (counts[key] || 0) + 1;
      });

      Object.entries(counts).forEach(([vid, qty]) => {
        orderBumpsPayload.push({
          productId: group.productId,
          quantity: qty,
          title: group.title,
          description: group.description,
          price: group.unitPriceCents,
          preSelected: group.preSelected,
          checked: group.checked,
          ...(group.maxQuantity != null ? { maxQuantity: group.maxQuantity } : {}),
          ...(vid ? { variantId: vid } : {}),
        });
        optInsPayload.push(group.checked);
      });
    } else {
      orderBumpsPayload.push({
        title: group.title,
        description: group.description,
        price: group.unitPriceCents,
        quantity: group.quantity,
        preSelected: group.preSelected,
        checked: group.checked,
        ...(group.maxQuantity != null ? { maxQuantity: group.maxQuantity } : {}),
      });
      optInsPayload.push(group.checked);
    }
  });

  return { orderBumpsPayload, optInsPayload };
}

export function CheckoutFlow({
  checkoutId,
  products,
  lineItems: checkoutLineItems = [],
  checkoutSession,
  stripe,
  checkoutSettings,
  paymentIntentLastErrorMessage,
  setupIntentLastErrorMessage,
}: CheckoutFlowProps) {
  if (!stripe?.publishableKey?.trim()) {
    throw new Error("No Stripe configuration found for this artist");
  }

  const savePaymentMethod = checkoutSession?.savePaymentMethod === true;
  const { step: stepParam } = useParams<{ step: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const step = stepParam ? (STEP_MAP[stepParam as keyof typeof STEP_MAP] ?? 1) : 1;
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    buildInitialQuantities({ products, checkoutLineItems })
  );
  const [customerEmail, setCustomerEmail] = useState(() => {
    const { email: urlEmail } = contactPrefillFromSearchParams(searchParams);
    const { email: storageEmail } = contactPrefillFromSessionStorage();
    return urlEmail || checkoutSession?.customerEmail || storageEmail || "";
  });
  const [customerPhone, setCustomerPhone] = useState(() => {
    const { phone: urlPhone } = contactPrefillFromSearchParams(searchParams);
    const { phone: storagePhone } = contactPrefillFromSessionStorage();
    return urlPhone || checkoutSession?.customerPhone || storagePhone || "";
  });
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>(() =>
    buildInitialCustomFieldValues(checkoutSession)
  );
  const [amountToPay, setAmountToPay] = useState("");
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(() => {
    const { address } = shippingSnapshotToStripe(checkoutSession);
    const { name: storageName } = contactPrefillFromSessionStorage();
    if (address) {
      if (!address.name?.trim() && storageName) {
        return { ...address, name: storageName };
      }
      return address;
    }
    if (storageName) {
      return {
        name: storageName,
        address: {
          line1: "",
          city: "",
          state: "",
          country: "",
          postal_code: "",
        },
      };
    }
    return null;
  });
  const [isAddressComplete, setIsAddressComplete] = useState(() => {
    const { addressComplete } = shippingSnapshotToStripe(checkoutSession);
    return addressComplete;
  });
  const [shippingProtectionOptIn, setShippingProtectionOptIn] = useState(true);

  const [bumpGroups, setBumpGroups] = useState<UIBumpGroup[]>(() => {
    const offers = checkoutSession?.orderBumpOffers ?? [];
    const optIns = checkoutSession?.orderBumpsOptIn ?? offers.map((o: any) => o.preSelected === true);
    const groupsMap = new Map<string, UIBumpGroup>();

    offers.forEach((offer: any, index: number) => {
      const isProd = !!offer.productId;
      const key = isProd ? offer.productId! : offer.title;
      const optIn = optIns[index] === true;

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          id: key,
          productId: offer.productId,
          title: offer.title.replace(/\s*\([^)]*\)$/, ""),
          description: offer.description,
          imageUrl: offer.imageUrl,
          variants: offer.variants,
          unitPriceCents: offer.unitPriceCents,
          preSelected: offer.preSelected,
          checked: optIn,
          quantity: 0,
          selectedVariantIds: [],
          maxQuantity: offer.maxQuantity,
        });
      }

      const group = groupsMap.get(key)!;
      if (optIn) {
        group.checked = true;
      }

      const qty = offer.quantity ?? 1;
      group.quantity += qty;
      for (let q = 0; q < qty; q++) {
        group.selectedVariantIds.push(offer.selectedVariantId ?? "");
      }
    });

    return Array.from(groupsMap.values());
  });

  const [bumpVariantHighlightLines, setBumpVariantHighlightLines] = useState<Set<number>>(() => new Set());

  const lastCheckoutId = useRef<string | null>(null);
  useEffect(() => {
    if (checkoutSession && checkoutSession._id !== lastCheckoutId.current) {
      lastCheckoutId.current = checkoutSession._id ?? null;
      const offers = checkoutSession.orderBumpOffers ?? [];
      const optIns = checkoutSession.orderBumpsOptIn ?? offers.map((o: any) => o.preSelected === true);

      const groupsMap = new Map<string, UIBumpGroup>();
      offers.forEach((offer: any, index: number) => {
        const isProd = !!offer.productId;
        const key = isProd ? offer.productId! : offer.title;
        const optIn = optIns[index] === true;

        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            id: key,
            productId: offer.productId,
            title: offer.title.replace(/\s*\([^)]*\)$/, ""),
            description: offer.description,
            imageUrl: offer.imageUrl,
            variants: offer.variants,
            unitPriceCents: offer.unitPriceCents,
            preSelected: offer.preSelected,
            checked: optIn,
            quantity: 0,
            selectedVariantIds: [],
            maxQuantity: offer.maxQuantity,
          });
        }

        const group = groupsMap.get(key)!;
        if (optIn) {
          group.checked = true;
        }

        const qty = offer.quantity ?? 1;
        group.quantity += qty;
        for (let q = 0; q < qty; q++) {
          group.selectedVariantIds.push(offer.selectedVariantId ?? "");
        }
      });

      setBumpGroups(Array.from(groupsMap.values()));
      setBumpVariantHighlightLines(new Set());
    }
  }, [checkoutSession]);

  const orderBumpsOptIn = useMemo(() => {
    const { optInsPayload } = serializeBumpGroups(bumpGroups);
    return optInsPayload;
  }, [bumpGroups]);

  const resolvedOrderBumpOffers = useMemo(() => {
    return bumpGroups.map((group, idx) => {
      let lineTotalCents = 0;
      let pricePendingVariant = false;
      const variantNeedsAttention = bumpVariantHighlightLines.has(idx);

      if (group.variants?.length) {
        group.selectedVariantIds.forEach((vid) => {
          if (!vid) {
            pricePendingVariant = true;
          } else {
            const variant = group.variants!.find((v) => v._id === vid);
            lineTotalCents += variant?.price != null ? Math.round(variant.price) : group.unitPriceCents;
          }
        });
      } else {
        lineTotalCents = group.unitPriceCents * group.quantity;
      }

      return {
        id: group.id,
        productId: group.productId,
        title: group.title,
        description: group.description,
        imageUrl: group.imageUrl,
        variants: group.variants,
        selectedVariantIds: group.selectedVariantIds,
        quantity: group.quantity,
        maxQuantity: group.maxQuantity,
        lineTotalCents: pricePendingVariant ? 0 : lineTotalCents,
        pricePendingVariant,
        variantSelectNeedsAttention: variantNeedsAttention,
        preSelected: group.preSelected,
        checked: group.checked,
      };
    });
  }, [bumpGroups, bumpVariantHighlightLines]);

  const persistOrderBumpsTimerRef = useRef<number | null>(null);
  const lastPersistedOrderBumpsKeyRef = useRef("");

  const persistOrderBumps = useCallback(async (currentGroups: UIBumpGroup[]): Promise<
    { ok: true } | { ok: false; error: string }
  > => {
    if (!checkoutId || currentGroups.length === 0) {
      return { ok: true };
    }

    const hasMissingBumpVariant = currentGroups.some(
      (g) => g.checked && g.variants?.length && g.selectedVariantIds.some((vid) => !vid)
    );
    if (hasMissingBumpVariant) {
      return { ok: true };
    }

    const { orderBumpsPayload } = serializeBumpGroups(currentGroups);
    const key = JSON.stringify(orderBumpsPayload);
    if (key === lastPersistedOrderBumpsKeyRef.current) {
      return { ok: true };
    }

    try {
      const res = await fetch(`/api/checkout/${checkoutId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderBumps: orderBumpsPayload }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        return {
          ok: false,
          error: err.error || "Could not update order bumps.",
        };
      }
      lastPersistedOrderBumpsKeyRef.current = key;
      if (stepRef.current === 3) {
        const data = (await res.json().catch(() => ({}))) as {
          clientSecret?: string;
        };
        if (typeof data.clientSecret === "string" && data.clientSecret !== "") {
          setHostedClientSecret(data.clientSecret);
        }
      }
      return { ok: true };
    } catch {
      return { ok: false, error: "Could not update order bumps." };
    }
  }, [checkoutId]);

  const schedulePersistOrderBumps = useCallback((currentGroups: UIBumpGroup[]) => {
    if (persistOrderBumpsTimerRef.current != null) {
      window.clearTimeout(persistOrderBumpsTimerRef.current);
    }
    persistOrderBumpsTimerRef.current = window.setTimeout(() => {
      persistOrderBumpsTimerRef.current = null;
      void persistOrderBumps(currentGroups);
    }, 450);
  }, [persistOrderBumps]);

  const handleOrderBumpQuantityChange = useCallback((groupIndex: number, newQty: number) => {
    let nextGroups: UIBumpGroup[] = [];
    setBumpGroups((prev) => {
      const next = prev.map((g, idx) => {
        if (idx !== groupIndex) return g;
        const currentIds = [...g.selectedVariantIds];
        if (newQty > g.quantity) {
          for (let i = g.quantity; i < newQty; i++) {
            currentIds.push("");
          }
        } else if (newQty < g.quantity) {
          currentIds.splice(newQty);
        }
        return {
          ...g,
          quantity: newQty,
          selectedVariantIds: currentIds,
        };
      });
      nextGroups = next;
      return next;
    });
    schedulePersistOrderBumps(nextGroups);
  }, [schedulePersistOrderBumps]);

  const handleOrderBumpVariantChange = useCallback((groupIndex: number, slotIndex: number, variantId: string) => {
    let nextGroups: UIBumpGroup[] = [];
    setBumpGroups((prev) => {
      const next = prev.map((g, idx) => {
        if (idx !== groupIndex) return g;
        const currentIds = [...g.selectedVariantIds];
        currentIds[slotIndex] = variantId;
        return {
          ...g,
          selectedVariantIds: currentIds,
        };
      });
      nextGroups = next;
      return next;
    });
    setBumpVariantHighlightLines((prevLines) => {
      const nextLines = new Set(prevLines);
      nextLines.delete(groupIndex);
      return nextLines;
    });
    schedulePersistOrderBumps(nextGroups);
  }, [schedulePersistOrderBumps]);

  const handleOrderBumpChangePlural = useCallback((groupIndex: number, checked: boolean) => {
    let nextGroups: UIBumpGroup[] = [];
    setBumpGroups((prev) => {
      const next = prev.map((g, idx) => {
        if (idx !== groupIndex) return g;
        return {
          ...g,
          checked,
        };
      });
      nextGroups = next;
      return next;
    });
    setBumpVariantHighlightLines((prevLines) => {
      const nextLines = new Set(prevLines);
      nextLines.delete(groupIndex);
      return nextLines;
    });
    schedulePersistOrderBumps(nextGroups);
  }, [schedulePersistOrderBumps]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(() => {
    const piErr = paymentIntentLastErrorMessage?.trim();
    if (piErr) return piErr;
    const siErr = setupIntentLastErrorMessage?.trim();
    if (siErr) return siErr;
    return null;
  });
  const [stepSaveError, setStepSaveError] = useState<string | null>(null);
  const [isSavingStep, setIsSavingStep] = useState(false);
  const [hostedClientSecret, setHostedClientSecret] = useState<string | null>(null);
  const [hostedSessionError, setHostedSessionError] = useState<string | null>(null);
  const [hostedTipCents, setHostedTipCents] = useState(0);
  const [tipSelection, setTipSelection] = useState<{ optedIn: boolean; hasSelected: boolean }>(() => {
    const isDefault =
      checkoutSettings?.tipping?.enabled === true &&
      (checkoutSession?.tippingOffer?.defaultSelected !== undefined
        ? checkoutSession.tippingOffer.defaultSelected
        : checkoutSettings?.tipping?.defaultSelected === true);
    return {
      optedIn: isDefault,
      hasSelected: !isDefault,
    };
  });

  const lastSettingsEnabled = useRef(checkoutSettings?.tipping?.enabled);
  const lastSettingsDefault = useRef(checkoutSettings?.tipping?.defaultSelected);
  const lastTippingOfferDefault = useRef(checkoutSession?.tippingOffer?.defaultSelected);
  useEffect(() => {
    if (
      checkoutSettings?.tipping?.enabled !== lastSettingsEnabled.current ||
      checkoutSettings?.tipping?.defaultSelected !== lastSettingsDefault.current ||
      checkoutSession?.tippingOffer?.defaultSelected !== lastTippingOfferDefault.current
    ) {
      lastSettingsEnabled.current = checkoutSettings?.tipping?.enabled;
      lastSettingsDefault.current = checkoutSettings?.tipping?.defaultSelected;
      lastTippingOfferDefault.current = checkoutSession?.tippingOffer?.defaultSelected;
      const isDefault =
        checkoutSettings?.tipping?.enabled === true &&
        (checkoutSession?.tippingOffer?.defaultSelected !== undefined
          ? checkoutSession.tippingOffer.defaultSelected
          : checkoutSettings?.tipping?.defaultSelected === true);
      setTipSelection({
        optedIn: isDefault,
        hasSelected: !isDefault,
      });
    }
  }, [
    checkoutSettings?.tipping?.enabled,
    checkoutSettings?.tipping?.defaultSelected,
    checkoutSession?.tippingOffer?.defaultSelected,
  ]);

  const stripePromise = useMemo(() => {
    const options: { stripeAccount?: string } = {};
    if (stripe.accountId) options.stripeAccount = stripe.accountId;
    return loadStripe(stripe.publishableKey, options);
  }, [stripe]);

  const checkoutProductSignature = useMemo(
    () => products.map((p) => String(p._id)).join("|"),
    [products]
  );

  const checkoutQuantityInitSignature = useMemo(
    () =>
      products
        .map((product, index) => {
          const line = checkoutLineItems[index];
          const q =
            line &&
            String(line.productId) === String(product._id) &&
            typeof line.quantity === "number" &&
            Number.isFinite(line.quantity)
              ? line.quantity
              : "";
          return `${product._id}:${q}`;
        })
        .join("|"),
    [products, checkoutLineItems]
  );

  const [visibleProductIndices, setVisibleProductIndices] = useState<number[]>(() =>
    products.map((_, i) => i)
  );

  useEffect(() => {
    setVisibleProductIndices(products.map((_, i) => i));
  }, [checkoutProductSignature]);

  const cartProducts = useMemo(
    () => visibleProductIndices.map((i) => products[i]).filter((p): p is Product => p !== undefined),
    [visibleProductIndices, products]
  );

  const cartCheckoutLineItems = useMemo(
    () => visibleProductIndices.map((i) => checkoutLineItems[i]).filter((l): l is NonNullable<typeof l> => l !== undefined),
    [visibleProductIndices, checkoutLineItems]
  );

  const lineCartVariantInitSignature = useMemo(
    () =>
      visibleProductIndices
        .map(
          (srcIdx, displayIdx) =>
            `${displayIdx}:${products[srcIdx]?._id ?? ""}:${checkoutLineItems[srcIdx]?.variantId ?? ""}`
        )
        .join("|"),
    [visibleProductIndices, products, checkoutLineItems]
  );

  const [lineVariantIds, setLineVariantIds] = useState<Record<number, string>>({});

  const visibleProductIndicesRef = useRef(visibleProductIndices);
  visibleProductIndicesRef.current = visibleProductIndices;
  const lineVariantIdsRef = useRef(lineVariantIds);
  lineVariantIdsRef.current = lineVariantIds;
  const quantitiesRef = useRef(quantities);
  quantitiesRef.current = quantities;
  const stepRef = useRef(step);
  stepRef.current = step;
  const persistLineItemsTimerRef = useRef<number | null>(null);
  const lastPersistedLineItemsKeyRef = useRef(
    JSON.stringify(
      buildLineItemsPayload({
        products,
        visibleProductIndices: products.map((_, i) => i),
        lineVariantIds: {},
        checkoutLineItems,
        quantities,
      })
    )
  );

  const [payVariantHighlightLines, setPayVariantHighlightLines] = useState(
    () => new Set<number>()
  );

  useEffect(() => {
    setLineVariantIds(() => {
      const next: Record<number, string> = {};
      cartProducts.forEach((p, i) => {
        const variants = p.variants;
        if (!variants?.length) return;
        if (variants.length === 1) {
          const only = variants[0];
          if (variantIsPurchasableForProduct(p, only)) next[i] = String(only._id);
          return;
        }
        const line = cartCheckoutLineItems[i];
        const pidOk = line && String(line.productId) === String(p._id);
        const fromLine = pidOk ? line.variantId?.trim() : undefined;
        const match =
          fromLine != null ? variants.find((v) => String(v._id) === fromLine) : undefined;
        const valid = match != null && variantIsPurchasableForProduct(p, match);
        if (valid && fromLine) next[i] = fromLine;
      });
      return next;
    });
    setPayVariantHighlightLines(new Set());
  }, [lineCartVariantInitSignature]);

  const persistCheckoutLineItems = useCallback(async (): Promise<
    { ok: true } | { ok: false; error: string }
  > => {
    if (!checkoutId || products.length === 0) {
      return { ok: true };
    }

    const hasMissingBumpVariant = resolvedOrderBumpOffers.some(
      (bump) => bump.checked && bump.pricePendingVariant
    );
    if (hasMissingBumpVariant) {
      return { ok: true };
    }

    const payload = buildLineItemsPayload({
      products,
      visibleProductIndices: visibleProductIndicesRef.current,
      lineVariantIds: lineVariantIdsRef.current,
      checkoutLineItems,
      quantities: quantitiesRef.current,
    });
    const key = JSON.stringify(payload);
    if (key === lastPersistedLineItemsKeyRef.current) {
      return { ok: true };
    }

    try {
      const res = await fetch(`/api/checkout/${checkoutId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lineItems: payload }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        return {
          ok: false,
          error: err.error || "Could not update checkout items.",
        };
      }
      lastPersistedLineItemsKeyRef.current = key;
      if (stepRef.current === 3) {
        const data = (await res.json().catch(() => ({}))) as {
          clientSecret?: string;
        };
        if (typeof data.clientSecret === "string" && data.clientSecret !== "") {
          setHostedClientSecret(data.clientSecret);
        }
      }
      return { ok: true };
    } catch {
      return { ok: false, error: "Could not update checkout items." };
    }
  }, [checkoutId, products, checkoutLineItems, resolvedOrderBumpOffers]);

  const schedulePersistCheckoutLineItems = useCallback(() => {
    if (persistLineItemsTimerRef.current != null) {
      window.clearTimeout(persistLineItemsTimerRef.current);
    }
    persistLineItemsTimerRef.current = window.setTimeout(() => {
      persistLineItemsTimerRef.current = null;
      void persistCheckoutLineItems();
    }, 450);
  }, [persistCheckoutLineItems]);

  const handleLineVariantChange = useCallback(
    ({
      displayIndex,
      variantId,
    }: {
      displayIndex: number;
      variantId: string;
    }) => {
      const nextVariantIds = {
        ...lineVariantIdsRef.current,
        [displayIndex]: variantId,
      };
      lineVariantIdsRef.current = nextVariantIds;
      setLineVariantIds(nextVariantIds);
      const product = cartProducts[displayIndex];
      if (product) {
        setQuantities((prev) => ({ ...prev, [product._id]: 1 }));
      }
      setPayVariantHighlightLines((prev) => {
        const next = new Set(prev);
        next.delete(displayIndex);
        return next;
      });
      setError(null);
      schedulePersistCheckoutLineItems();
    },
    [cartProducts, schedulePersistCheckoutLineItems]
  );

  const lineDisplay = useMemo(() => {
    return cartProducts.map((p, i) => {
      const line = cartCheckoutLineItems[i];
      const variants = p.variants;
      const vid = resolveCartLineVariantId({
        product: p,
        checkoutLineItem: line,
        lineVariantIds,
        displayIndex: i,
      });
      const v = vid ? p.variants?.find((x) => String(x._id) === vid) : undefined;
      const variantResolved = !variants?.length || (!!vid && !!v);
      const unitPrice = variantResolved ? unitPriceCents(p, vid) : 0;
      const baseName = p.name || "Product";
      return {
        variantId: v && vid ? vid : undefined,
        variantResolved,
        unitPrice,
        baseName,
      };
    });
  }, [cartProducts, cartCheckoutLineItems, lineVariantIds]);

  const variantSelectionsComplete = useMemo(
    () => lineDisplay.every((row) => row.variantResolved),
    [lineDisplay]
  );

  const validateVariantsForPayment = useCallback(() => {
    const missing: number[] = [];
    cartProducts.forEach((p, i) => {
      if (!p.variants?.length) return;
      if (!lineDisplay[i].variantId) missing.push(i);
    });
    if (missing.length > 0) {
      setPayVariantHighlightLines(new Set(missing));
      return {
        ok: false as const,
        message:
          "Please choose an option for each product before placing your order.",
      };
    }

    const missingBumps: number[] = [];
    resolvedOrderBumpOffers.forEach((bump, idx) => {
      if (orderBumpsOptIn[idx] === true && bump.pricePendingVariant) {
        missingBumps.push(idx);
      }
    });
    if (missingBumps.length > 0) {
      setBumpVariantHighlightLines(new Set(missingBumps));
      return {
        ok: false as const,
        message: "Please choose an option for each selected order bump before placing your order.",
      };
    }

    setPayVariantHighlightLines(new Set());
    setBumpVariantHighlightLines(new Set());
    return { ok: true as const };
  }, [cartProducts, lineDisplay, resolvedOrderBumpOffers, orderBumpsOptIn]);

  const displayProducts = useMemo(
    () =>
      cartProducts.map((p, i) => ({
        ...p,
        price: lineDisplay[i].unitPrice,
        pricePendingVariant:
          !!p.variants?.length && !lineDisplay[i].variantResolved,
        name: lineDisplay[i].baseName,
        selectedVariantId: lineDisplay[i].variantId,
        variantSelectNeedsAttention: payVariantHighlightLines.has(i),
      })),
    [cartProducts, lineDisplay, payVariantHighlightLines]
  );

  const getMetaPixelLineItems = useCallback(
    () =>
      cartProducts
        .map((product, index) => ({
          productId: product._id,
          quantity: quantities[product._id] || 0,
          unitPriceCents: lineDisplay[index].unitPrice,
          variantResolved: lineDisplay[index].variantResolved,
        }))
        .filter((row) => row.quantity > 0 && row.variantResolved)
        .map(({ variantResolved: _r, ...rest }) => rest),
    [cartProducts, quantities, lineDisplay]
  );

  const metaPixelId = checkoutSettings?.metaPixelId?.trim() ?? "";
  const metaPixelTracked = useRef({
    initiateCheckout: false,
    addShippingInfo: false,
  });

  useEffect(() => {
    metaPixelTracked.current = {
      initiateCheckout: false,
      addShippingInfo: false,
    };
  }, [checkoutId]);

  useEffect(() => {
    if (!metaPixelId) return;
    initMetaPixelCheckout({ pixelId: metaPixelId });
  }, [metaPixelId]);

  useEffect(() => {
    if (products.length === 0) return;
    setQuantities(buildInitialQuantities({ products, checkoutLineItems }));
  }, [checkoutQuantityInitSignature, products, checkoutLineItems]);

  const handleRemoveLineFromCart = ({
    displayIndex,
  }: {
    displayIndex: number;
  }) => {
    if (displayIndex <= 0) return;
    const sourceIndex = visibleProductIndices[displayIndex];
    const removed = sourceIndex != null ? products[sourceIndex] : undefined;
    if (!removed) return;
    if (!window.confirm("Are you sure you want to remove this item?")) return;
    const nextVisible = visibleProductIndicesRef.current.filter(
      (idx) => idx !== sourceIndex
    );
    visibleProductIndicesRef.current = nextVisible;
    setVisibleProductIndices(nextVisible);
    setQuantities((prev) => {
      const next = { ...prev };
      delete next[removed._id];
      return next;
    });
    window.setTimeout(() => {
      void persistCheckoutLineItems();
    }, 0);
  };

  const handleQuantityChange = (productId: string, quantity: number) => {
    const product = products.find((p) => p._id === productId);
    const min = product?.quantityRules?.minimum ?? 1;
    const max = product?.quantityRules?.maximum ?? 10;
    const clamped = Math.max(min, Math.min(max, quantity));
    setQuantities((prev) => ({ ...prev, [productId]: clamped }));
  };

  const buildShippingPayload = (addr: ShippingAddress) => ({
    name: addr.name ?? "",
    address: {
      line1: addr.address?.line1 ?? "",
      ...(addr.address?.line2 != null && String(addr.address.line2).trim() !== ""
        ? { line2: addr.address.line2 }
        : {}),
      city: addr.address?.city ?? "",
      state: addr.address?.state ?? "",
      country: addr.address?.country ?? "",
      postal_code: addr.address?.postal_code ?? "",
    },
  });

  const handleNext = async () => {
    setStepSaveError(null);
    if (step === 1) {
      if (!variantSelectionsComplete) {
        const vchk = validateVariantsForPayment();
        if (!vchk.ok) {
          setStepSaveError(vchk.message);
          return;
        }
      }
      setIsSavingStep(true);
      try {
        const lineItemsPersist = await persistCheckoutLineItems();
        if (!lineItemsPersist.ok) {
          setStepSaveError(lineItemsPersist.error);
          return;
        }
        const orderBumpsPersist = await persistOrderBumps(bumpGroups);
        if (!orderBumpsPersist.ok) {
          setStepSaveError(orderBumpsPersist.error);
          return;
        }
        const defs = checkoutSession?.contactCustomFields;
        const response = await fetch(`/api/checkout/${checkoutId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contact: {
              email: customerEmail.trim(),
              phone: customerPhone.trim(),
              ...(defs != null && defs.length > 0
                ? { customFieldValues }
                : {}),
            },
          }),
        });
        if (!response.ok) {
          const err = (await response.json().catch(() => ({}))) as { error?: string };
          setStepSaveError(err.error || "Could not save your contact information.");
          return;
        }
        const pixel = checkoutSettings?.metaPixelId?.trim() ?? "";
        if (pixel) {
          const items = getMetaPixelLineItems();
          trackMetaPixel({
            event: "Lead",
            payload:
              items.length > 0
                ? {
                    ...metaPixelCheckoutPayload({
                      valueCents: getEffectiveTotal(),
                      items,
                    }),
                    content_name: "Checkout contact",
                  }
                : { content_name: "Checkout contact", value: 0, currency: "USD" },
          });
        }
        navigate(
          skipsShipping
            ? `/checkout/${checkoutId}/payment`
            : `/checkout/${checkoutId}/shipping`
        );
      } finally {
        setIsSavingStep(false);
      }
    } else if (step === 2) {
      const vchk = validateVariantsForPayment();
      if (!vchk.ok) {
        setStepSaveError(vchk.message);
        return;
      }
      if (!shippingAddress) return;
      setIsSavingStep(true);
      try {
        const lineItemsPersist = await persistCheckoutLineItems();
        if (!lineItemsPersist.ok) {
          setStepSaveError(lineItemsPersist.error);
          return;
        }
        const orderBumpsPersist = await persistOrderBumps(bumpGroups);
        if (!orderBumpsPersist.ok) {
          setStepSaveError(orderBumpsPersist.error);
          return;
        }
        const response = await fetch(`/api/checkout/${checkoutId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shipping: buildShippingPayload(
              shippingAddressWithoutPhone(shippingAddress)
            ),
          }),
        });
        if (!response.ok) {
          const err = (await response.json().catch(() => ({}))) as { error?: string };
          setStepSaveError(err.error || "Could not save your shipping information.");
          return;
        }
        const pixel = checkoutSettings?.metaPixelId?.trim() ?? "";
        if (pixel && !metaPixelTracked.current.addShippingInfo) {
          metaPixelTracked.current.addShippingInfo = true;
          const items = cartProducts
            .map((product, index) => ({
              productId: product._id,
              quantity: quantities[product._id] || 0,
              unitPriceCents: lineDisplay[index].unitPrice,
              variantResolved: lineDisplay[index].variantResolved,
            }))
            .filter((row) => row.quantity > 0 && row.variantResolved)
            .map(({ variantResolved: _r, ...rest }) => rest);
          trackMetaPixel({
            event: "AddShippingInfo",
            payload: metaPixelCheckoutPayload({
              valueCents: getEffectiveTotal(),
              items,
            }),
          });
        }
        navigate(`/checkout/${checkoutId}/payment`);
      } finally {
        setIsSavingStep(false);
      }
    }
  };

  const getCartItems = () => {
    return cartProducts
      .map((product, index) => {
        const dp = displayProducts[index];
        const ld = lineDisplay[index];
        if (!dp || !ld) return null;
        return {
          product: dp,
          quantity: quantities[product._id] || 0,
          variantId: ld.variantId,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item != null && item.quantity > 0);
  };

  const sessionCartKey = useMemo(
    () =>
      JSON.stringify(
        cartProducts.map((product, index) => ({
          productId: product._id,
          quantity: quantities[product._id] || 0,
          variantId: lineDisplay[index].variantId,
        }))
      ),
    [quantities, cartProducts, lineDisplay]
  );

  const skipsShipping =
    getCartItems().length > 0 &&
    getCartItems().every(
      (item) => item.product.deliveryType === DeliveryTypeEnum.DIGITAL
    );

  const shippingQuotePhysical = useMemo(() => {
    if (skipsShipping) return { cents: 0, label: "" };
    const ids = [
      ...new Set(
        cartProducts.flatMap((p, index) => {
          const qty = quantities[p._id] || 0;
          if (qty <= 0 || p.deliveryType === DeliveryTypeEnum.DIGITAL) return [];
          return [p._id];
        })
      ),
    ];

    return getShippingForPhysicalCart({
      physicalProductIds: ids,
      shippingProfiles: checkoutSettings?.shippingProfiles ?? null,
      shipToCountryCode: shippingAddress?.address?.country?.trim() || null,
    });
  }, [
    skipsShipping,
    checkoutSettings?.shippingProfiles,
    cartProducts,
    quantities,
    shippingAddress?.address?.country,
  ]);

  const shippingProtectionUnitCents = Math.max(
    0,
    Math.round(checkoutSettings?.shippingProtection?.price ?? 0)
  );
  const shippingProtectionSelectable =
    !skipsShipping &&
    checkoutSettings?.shippingProtection?.enabled === true &&
    shippingProtectionUnitCents > 0;

  const getShippingProtectionCents = () =>
    shippingProtectionSelectable && shippingProtectionOptIn ? shippingProtectionUnitCents : 0;

  const getOrderBumpCents = () => {
    let sum = 0;
    for (let i = 0; i < resolvedOrderBumpOffers.length; i++) {
      if (orderBumpsOptIn[i] === true) {
        sum += Math.max(0, Math.round(resolvedOrderBumpOffers[i].lineTotalCents ?? 0));
      }
    }
    return sum;
  };

  const tippingOffer = checkoutSession?.tippingOffer;
  const tipTitle =
    tippingOffer?.title?.trim() ||
    checkoutSettings?.tipping?.title?.trim() ||
    "Add tip";
  const tipDescription =
    tippingOffer?.description?.trim() ||
    checkoutSettings?.tipping?.description?.trim() ||
    undefined;

  const getTotal = () => {
    return getCartItems().reduce((sum, item) => {
      const prod = item.product as {
        price?: number;
        pricePendingVariant?: boolean;
      };
      if (prod.pricePendingVariant) return sum;
      return sum + (prod.price || 0) * item.quantity;
    }, 0);
  };

  const getProductChargeCents = () => {
    const hasPayWhatYouCan = cartProducts.some((p) => p.payWhatYouCan);
    const subtotal = getTotal();
    if (hasPayWhatYouCan && amountToPay) {
      const parsed = parseFloat(amountToPay);
      const cents = Math.round(parsed * 100);
      return !Number.isNaN(parsed) && cents >= subtotal ? cents : subtotal;
    }
    return subtotal;
  };

  const getShippingCents = () => shippingQuotePhysical.cents;

  const getShippingLabel = () => shippingQuotePhysical.label;

  const getEffectiveTotal = () =>
    getProductChargeCents() +
    getShippingCents() +
    getShippingProtectionCents() +
    getOrderBumpCents();

  const submitHostedOrder = async ({
    tipAmountCents,
    paymentIntentId,
    setupIntentId,
  }: {
    tipAmountCents?: number;
    paymentIntentId?: string;
    setupIntentId?: string;
  }) => {
    try {
      const { orderBumpsPayload } = serializeBumpGroups(bumpGroups);
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          checkoutId,
          products: getCartItems().map((item) => ({
            productId: item.product._id,
            quantity: item.quantity,
            ...(item.variantId ? { variantId: item.variantId } : {}),
          })),
          email: customerEmail,
          phone: customerPhone,
          shipping: shippingAddress
            ? shippingAddressWithoutPhone(shippingAddress)
            : undefined,
          total: getEffectiveTotal(),
          shippingProtection:
            !skipsShipping &&
            checkoutSettings?.shippingProtection?.enabled === true &&
            shippingProtectionUnitCents > 0
              ? shippingProtectionOptIn
              : false,
          orderBumps: orderBumpsPayload,
          ...(tipAmountCents != null && tipAmountCents > 0
            ? { tipAmount: tipAmountCents }
            : {}),
          ...(paymentIntentId ? { paymentIntentId } : {}),
          ...(setupIntentId ? { setupIntentId } : {}),
        }),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(
          errorData.error ||
            (savePaymentMethod ? "Card setup failed" : "Payment failed")
        );
      }

      const data = (await response.json()) as { goTo?: string };
      const goTo =
        typeof data.goTo === "string" && data.goTo.trim() !== ""
          ? data.goTo.trim()
          : null;
      if (!goTo) {
        window.alert(
          savePaymentMethod
            ? "Your card was saved, but no destination page is configured for this checkout. Please contact the seller."
            : "Your payment went through, but no destination page is configured for this checkout. Please contact the seller."
        );
        throw new Error("No destination configured after checkout");
      }
      if (!savePaymentMethod) {
        const pixel = checkoutSettings?.metaPixelId?.trim() ?? "";
        if (pixel) {
          const items = getCartItems().map((item) => ({
            productId: item.product?._id ?? "",
            quantity: item.quantity,
            unitPriceCents: item.product?.price ?? 0,
          }));
          trackMetaPixel({
            event: "Purchase",
            payload: metaPixelCheckoutPayload({
              valueCents: getEffectiveTotal() + (tipAmountCents ?? 0),
              items,
            }),
          });
        }
      }
      window.location.replace(goTo);
    } catch (err) {
      throw err;
    }
  };

  const canProceedToNextStep = () => {
    if (step === 1) {
      const hasPayWhatYouCan = cartProducts.some((p) => p.payWhatYouCan);
      const emailOk = customerEmail.trim() !== "";
      const phoneOk = isValidPhoneNumber(customerPhone);
      const defs = checkoutSession?.contactCustomFields;
      if (defs != null && defs.length > 0) {
        for (const d of defs) {
          const v = (customFieldValues[d.key] ?? "").trim();
          if (d.required) {
            if (!v || !d.options.includes(v)) return false;
          } else if (v !== "" && !d.options.includes(v)) return false;
        }
      }
      if (!hasPayWhatYouCan) return emailOk && phoneOk;
      const parsed = parseFloat(amountToPay);
      const amountOk =
        !isNaN(parsed) && Math.round(parsed * 100) >= getTotal();
      return emailOk && phoneOk && amountOk;
    }
    if (step === 2 && !skipsShipping) {
      const addressOk =
        cartProducts.length > 0 &&
        Object.keys(quantities).length > 0 &&
        isAddressComplete &&
        shippingAddress !== null;
      if (!addressOk) return false;

      // Ensure checked order bumps have variants chosen
      const bumpsOk = resolvedOrderBumpOffers.every((bump, idx) => {
        if (orderBumpsOptIn[idx] === true) {
          return !bump.pricePendingVariant;
        }
        return true;
      });
      return bumpsOk;
    }
    return false;
  };

  const orderTotalForSync = getEffectiveTotal();

  useEffect(() => {
    if (!metaPixelId) return;
    if (metaPixelTracked.current.initiateCheckout) return;
    const items = getMetaPixelLineItems();
    if (items.length === 0) return;
    metaPixelTracked.current.initiateCheckout = true;
    trackMetaPixel({
      event: "InitiateCheckout",
      payload: metaPixelCheckoutPayload({
        valueCents: orderTotalForSync,
        items,
      }),
    });
  }, [metaPixelId, getMetaPixelLineItems, orderTotalForSync, sessionCartKey]);

  useEffect(() => {
    if (step !== 3) {
      setHostedClientSecret(null);
      setHostedSessionError(null);
      return;
    }
    if (!checkoutId) return;

    const isTipSelectionPending =
      checkoutSettings?.tipping?.enabled === true &&
      tipSelection.optedIn &&
      !tipSelection.hasSelected;

    if (isTipSelectionPending) {
      setHostedSessionError(null);
      setHostedClientSecret(null);
      return;
    }

    setHostedClientSecret(null);

    if (!variantSelectionsComplete) {
      setHostedSessionError(null);
      return;
    }

    const hasMissingBumpVariant = resolvedOrderBumpOffers.some(
      (bump) => bump.checked && bump.pricePendingVariant
    );
    if (hasMissingBumpVariant) {
      setHostedSessionError(null);
      return;
    }

    const cartItems = getCartItems();
    if (cartItems.length === 0) {
      setHostedSessionError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setHostedSessionError(null);
      try {
        const { orderBumpsPayload } = serializeBumpGroups(bumpGroups);
        const res = await fetch(`/api/checkout/${checkoutId}/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            products: cartItems.map((item) => ({
              productId: item.product._id,
              quantity: item.quantity,
              ...(item.variantId ? { variantId: item.variantId } : {}),
            })),
            total: orderTotalForSync,
            tipCents: hostedTipCents,
            shippingProtection:
              !skipsShipping &&
              checkoutSettings?.shippingProtection?.enabled === true &&
              shippingProtectionUnitCents > 0
                ? shippingProtectionOptIn
                : false,
            orderBumps: orderBumpsPayload,
          }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
          clientSecret?: string;
        };
        if (!res.ok) {
          throw new Error(data.error || "Could not prepare payment");
        }
        if (!data.clientSecret) {
          throw new Error("Missing payment session");
        }
        if (!cancelled) {
          setHostedClientSecret(data.clientSecret);
          setHostedSessionError(null);
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "Could not prepare payment";
          setHostedSessionError(msg);
          setHostedClientSecret(null);
          setError(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    step,
    checkoutId,
    stripePromise,
    hostedTipCents,
    shippingProtectionOptIn,
    orderBumpsOptIn,
    skipsShipping,
    shippingProtectionUnitCents,
    checkoutSettings?.shippingProtection?.enabled,
    orderTotalForSync,
    sessionCartKey,
    variantSelectionsComplete,
    checkoutSettings?.tipping?.enabled,
    tipSelection,
    resolvedOrderBumpOffers,
  ]);

  useEffect(() => {
    if (stepParam === "shipping" && skipsShipping) {
      navigate(`/checkout/${checkoutId}/payment`, { replace: true });
    }
  }, [stepParam, skipsShipping, checkoutId, navigate]);

  const getCustomerName = () => shippingAddress?.name ?? "";

  const totalSteps = skipsShipping ? 2 : 3;
  const currentStepDisplay = skipsShipping
    ? stepParam === "contact"
      ? 1
      : stepParam === "payment"
        ? 2
        : 1
    : step;

  const getStepNavPath = (s: number) => {
    if (skipsShipping) {
      if (s === 1) return `/checkout/${checkoutId}/contact`;
      if (s === 2) return `/checkout/${checkoutId}/payment`;
    } else {
      if (s === 1) return `/checkout/${checkoutId}/contact`;
      if (s === 2) return `/checkout/${checkoutId}/shipping`;
      if (s === 3) return `/checkout/${checkoutId}/payment`;
    }
    return `/checkout/${checkoutId}/contact`;
  };

  const backgroundColor = checkoutSettings?.colors?.backgroundColor || "#ffffff";
  const buttonColor = checkoutSettings?.colors?.buttonColor || "#000000";
  const accentColor = checkoutSettings?.colors?.accentColor || "#000000";
  const textColor = getContrastTextColor(backgroundColor);
  const accentTextColor = getContrastTextColor(accentColor);

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor }}>
      {metaPixelId ? (
        <noscript>
          <img
            height={1}
            width={1}
            alt=""
            style={{ display: "none" }}
            src={metaPixelNoScriptPageViewSrc({ pixelId: metaPixelId })}
          />
        </noscript>
      ) : null}
      <div
        className="max-w-4xl w-full mx-auto min-h-screen flex flex-col py-4 px-4"
        style={{ color: textColor }}
      >
      <h1 className="text-2xl font-bold text-center">Checkout</h1>
      <div className="flex items-center justify-center gap-0 py-4">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s, i) => (
          <div key={s} className="flex items-center">
            {i > 0 && (
              <div
                className="h-px w-8 shrink-0 mx-1"
                style={{ backgroundColor: accentColor, opacity: 0.4 }}
              />
            )}
            <button
              type="button"
              onClick={() => {
                if (!isProcessing && s < currentStepDisplay) {
                  navigate(getStepNavPath(s));
                }
              }}
              disabled={isProcessing || s > currentStepDisplay}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer disabled:cursor-not-allowed"
              style={
                s === currentStepDisplay
                  ? { backgroundColor: accentColor, color: accentTextColor }
                  : s < currentStepDisplay
                    ? {
                        border: "1px solid",
                        borderColor: accentColor,
                        backgroundColor: "transparent",
                        color: textColor,
                      }
                    : {
                        border: "1px solid rgba(128,128,128,0.3)",
                        backgroundColor: "rgba(128,128,128,0.2)",
                        color: "rgba(128,128,128,0.6)",
                      }
              }
            >
              {s}
            </button>
          </div>
        ))}
      </div>

      {step === 1 && (
        <CheckoutEmailStep
          email={customerEmail}
          onEmailChange={setCustomerEmail}
          phone={customerPhone}
          onPhoneChange={setCustomerPhone}
          products={displayProducts}
          quantities={quantities}
          onQuantityChange={handleQuantityChange}
          onRemoveLine={handleRemoveLineFromCart}
          onLineVariantChange={handleLineVariantChange}
          total={getTotal()}
          amountToPay={amountToPay}
          onAmountToPayChange={setAmountToPay}
          contactCustomFields={checkoutSession?.contactCustomFields}
          customFieldValues={customFieldValues}
          onCustomFieldValueChange={(key, value) =>
            setCustomFieldValues((prev) => ({ ...prev, [key]: value }))
          }
          checkoutColors={checkoutSettings?.colors ?? null}
        />
      )}

      {step === 2 && !skipsShipping && (
        <CheckoutShippingStep
          products={displayProducts}
          quantities={quantities}
          onQuantityChange={handleQuantityChange}
          onRemoveLine={handleRemoveLineFromCart}
          onLineVariantChange={handleLineVariantChange}
          shippingAddress={shippingAddress}
          onShippingAddressChange={setShippingAddress}
          onAddressCompleteChange={setIsAddressComplete}
          stripePromise={stripePromise}
          total={getProductChargeCents()}
          shippingCents={getShippingCents()}
          shippingLabel={getShippingLabel()}
          shippingProtectionCents={getShippingProtectionCents()}
          shippingProtectionSelectable={shippingProtectionSelectable}
          shippingProtectionUnitCents={shippingProtectionUnitCents}
          shippingProtectionChecked={shippingProtectionOptIn}
          onShippingProtectionChange={setShippingProtectionOptIn}
          orderBumpCents={getOrderBumpCents()}
          orderBumpSelectable={resolvedOrderBumpOffers.length > 0}
          orderBumps={resolvedOrderBumpOffers}
          orderBumpsOptIn={orderBumpsOptIn}
          onOrderBumpChangePlural={handleOrderBumpChangePlural}
          onOrderBumpQuantityChange={handleOrderBumpQuantityChange}
          onOrderBumpVariantChange={handleOrderBumpVariantChange}
          checkoutSettings={checkoutSettings}
        />
      )}

      {step === 3 && (
        <HostedCheckoutPayment
          stripePromise={stripePromise}
          clientSecret={hostedClientSecret}
          sessionError={hostedSessionError}
          variantSelectionsComplete={variantSelectionsComplete}
          validateVariantsForPayment={validateVariantsForPayment}
          elementsKey={`${hostedClientSecret ?? "pending"}-${orderTotalForSync}-${hostedTipCents}-${JSON.stringify(orderBumpsOptIn)}`}
          appearance={checkoutStripeElementsAppearance({
            backgroundColor,
            accentColor: checkoutSettings?.colors?.accentColor,
          })}
          products={displayProducts}
          quantities={quantities}
          onQuantityChange={handleQuantityChange}
          onRemoveLine={handleRemoveLineFromCart}
          onLineVariantChange={handleLineVariantChange}
          total={getProductChargeCents()}
          shippingCents={getShippingCents()}
          shippingLabel={getShippingLabel()}
          shippingProtectionCents={getShippingProtectionCents()}
          shippingProtectionSelectable={shippingProtectionSelectable}
          shippingProtectionUnitCents={shippingProtectionUnitCents}
          shippingProtectionChecked={shippingProtectionOptIn}
          onShippingProtectionChange={setShippingProtectionOptIn}
          orderBumpCents={getOrderBumpCents()}
          orderBumpSelectable={resolvedOrderBumpOffers.length > 0}
          orderBumps={resolvedOrderBumpOffers}
          orderBumpsOptIn={orderBumpsOptIn}
          onOrderBumpChangePlural={handleOrderBumpChangePlural}
          onOrderBumpQuantityChange={handleOrderBumpQuantityChange}
          onOrderBumpVariantChange={handleOrderBumpVariantChange}
          tipTitle={tipTitle}
          tipDescription={tipDescription}
          tipDefaultSelected={tippingOffer?.defaultSelected}
          name={getCustomerName()}
          email={customerEmail}
          onHostedTipAmountCentsChange={({ tipAmountCents }) =>
            setHostedTipCents(tipAmountCents)
          }
          onTipSelectionChange={setTipSelection}
          onFinalizeHostedCheckout={submitHostedOrder}
          isProcessing={isProcessing}
          error={error}
          setError={setError}
          setIsProcessing={setIsProcessing}
          checkoutSettings={checkoutSettings}
          submitBtnText={checkoutSession?.submitBtnText}
          paymentMode={savePaymentMethod ? "setup" : "payment"}
          checkoutId={checkoutId}
          getCartLinesForInventoryCheck={() =>
            getCartItems().map((item) => ({
              productId: item.product?._id ?? "",
              quantity: item.quantity,
              ...(item.variantId ? { variantId: item.variantId } : {}),
            }))
          }
        />
      )}

      {stepParam !== "payment" && (
        <div className="flex flex-col items-end gap-2 pt-4 mt-auto">
          {stepSaveError && (
            <p className="text-sm text-red-500 text-right w-full">{stepSaveError}</p>
          )}
          <Button
            type="button"
            onClick={() => void handleNext()}
            disabled={!canProceedToNextStep() || isSavingStep}
            style={{
              backgroundColor: buttonColor,
              color: getContrastTextColor(buttonColor),
            }}
            className="font-bold hover:opacity-90"
          >
            {isSavingStep ? "Saving…" : "Continue"}
          </Button>
        </div>
      )}
      </div>
    </div>
  );
}
